import { useEffect, useState, useRef } from 'react';

export const useAudioManager = ({ isDigitalHuman, isDesktop }: { isDigitalHuman?: boolean; isDesktop?: boolean }) => {
  const [audioQueue, setAudioQueue] = useState<string[]>([]);
  const audioQueueRef = useRef<string[]>([]); // 新增：用于追踪最新的队列
  const isPlayingRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioPlayIndexRef = useRef(0);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  // 当 audioQueue 更新时，同步更新 ref
  useEffect(() => {
    audioQueueRef.current = audioQueue;
  }, [audioQueue]);

  const playAudioWithDigitalHuman = async (audioUrl: string) => {
    return new Promise<void>((resolve, reject) => {
      window.avatarSpeak(
        audioUrl,
        () => {
          resolve();
        },
        (err) => {
          console.log('Digital Human Error: ' + err);
          reject(err);
        }
      );
    });
  };

  const processAudioQueue = async () => {
    // 确保清理之前的音频实例
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.onended = null;
      currentAudioRef.current.onerror = null;
      currentAudioRef.current = null;
    }
    if (audioPlayIndexRef.current >= audioQueueRef.current.length) {
      isPlayingRef.current = false;
      setIsPlaying(false);
      return;
    }

    isPlayingRef.current = true;
    setIsPlaying(true);

    const playNext = () => {
      audioPlayIndexRef.current++;
      setTimeout(() => processAudioQueue(), 200);
    };

    const audioUrl = audioQueueRef.current[audioPlayIndexRef.current];
    if (!audioUrl) {
      playNext();
      return;
    }

    // 数字人播放
    if (isDigitalHuman && window.avatarSpeak) {
      await playAudioWithDigitalHuman(audioUrl)
        .then(() => {
          playNext();
        })
        .catch(() => {
          playNext();
        });

      return;
    }

    // 普通音频播放
    const audio = new Audio(audioUrl);
    currentAudioRef.current = audio;

    audio.onended = () => {
      playNext();
    };

    audio.onerror = () => {
      playNext();
    };

    if (isDesktop) {
      // audio.muted = true;
      window.postMessage(
        {
          type: 'audioData',
          audioUrl: audioUrl,
        },
        '*'
      );
    }

    audio
      .play()
      .then(() => {
        console.log('audio play success', audioPlayIndexRef.current, audioUrl);
      })
      .catch((error) => {
        console.warn('play error:', error);
        playNext();
      });
  };

  useEffect(() => {
    if (audioQueue.length > 0 && !isPlayingRef.current) {
      processAudioQueue();
    }
  }, [audioQueue]);

  useEffect(() => {
    return () => {
      stopAudio();
      audioQueue.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const stopAudio = () => {
    if (isPlayingRef.current && isDigitalHuman && window.stopAvatarSpeaking) {
      window.stopAvatarSpeaking();
    }
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.onended = null;
      currentAudioRef.current.onerror = null;
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
  };

  return {
    audioQueue,
    setAudioQueue,
    isPlaying,
    stopAudio,
  };
};
